import { JoinGroupCard, JoinRequestsAdminCard } from "../components/JoinRequests";
import OfflineCard from "../components/OfflineCard";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Settings, User, Building2, Palette, LogOut, Plus, X, Check, Upload, Type, GraduationCap, HandCoins, CalendarSync, Copy } from "lucide-react";
import api, { API, logoUrl, withToken } from "@/lib/api";
import { subscribeToPush, pushSupported } from "@/lib/push";
import { Bell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { useAuth, applyTheme } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import TutorialCarousel from "@/components/TutorialCarousel";
import FeesConfig from "@/components/FeesConfig";
import MyFeesCard from "@/components/MyFeesCard";

const THEME_FIELDS = [
  { k: "bg_app", l: "param.theme_bg_app" },
  { k: "bg_sidebar", l: "param.theme_bg_sidebar" },
  { k: "bg_card", l: "param.theme_bg_card" },
  { k: "text_primary", l: "param.theme_text_primary" },
  { k: "text_secondary", l: "param.theme_text_secondary" },
  { k: "brand_primary", l: "param.theme_brand_primary" },
  { k: "brand_accent", l: "param.theme_brand_accent" },
  { k: "border_color", l: "param.theme_border_color" },
];

const PALETTES = [
  { name: "Émeraude Celtique", theme: { bg_app: "#090D16", bg_sidebar: "#0F172A", bg_card: "#1E293B", text_primary: "#F1F5F9", text_secondary: "#94A3B8", brand_primary: "#10B981", brand_accent: "#F59E0B", border_color: "#334155" } },
  { name: "Bleu de Bretagne", theme: { bg_app: "#0B1220", bg_sidebar: "#101B33", bg_card: "#182747", text_primary: "#EFF6FF", text_secondary: "#93A5C8", brand_primary: "#3B82F6", brand_accent: "#F59E0B", border_color: "#2A3D66" } },
  { name: "Lande & Bruyère", theme: { bg_app: "#14100F", bg_sidebar: "#1E1917", bg_card: "#2A2320", text_primary: "#F5F0EB", text_secondary: "#A89E94", brand_primary: "#84CC16", brand_accent: "#FB923C", border_color: "#443A34" } },
  { name: "Clair Parchment", theme: { bg_app: "#F5F2EA", bg_sidebar: "#EAE4D5", bg_card: "#FFFFFF", text_primary: "#1F2937", text_secondary: "#6B7280", brand_primary: "#059669", brand_accent: "#D97706", border_color: "#D8D0BC" } },
];

const EVENT_MAINS = [
  { k: "repetition", l: "cat.repetition" },
  { k: "prestation", l: "cat.prestation" },
  { k: "autre", l: "cat.autre" },
];

export default function ParametresPage() {
  const { user, group, tspace, refresh, logout, t, lang, setLang, identity } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManageFees = ["admin", "treasurer"].includes(user?.role);
  const isPersonal = group?.kind === "personal";
  const [params] = useSearchParams();
  const defaultTab = params.get("tab") || "profil";
  const navigate = useNavigate();
  // Progression des envois de photos (logo, images d'événement…) : clé -> 0..95 (null = terminé)
  const [upPct, setUpPct] = useState({});
  const uploadWithProgress = (key, path, fd) =>
    api.post(path, fd, { onUploadProgress: (e) => setUpPct((m) => ({ ...m, [key]: e.total ? Math.min(95, Math.round((e.loaded / e.total) * 100)) : 0 })) })
      .finally(() => setUpPct((m) => ({ ...m, [key]: null })));

  const UploadBar = ({ k }) => (upPct[k] == null ? null : (
    <div className="mt-2" data-testid={`upload-progress-${k}`}>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div className="h-full accent-bg transition-[width] duration-200" style={{ width: `${upPct[k]}%` }} />
      </div>
      <p className="text-xs t-secondary mt-1">{t("common.uploading")}… {upPct[k]}%</p>
    </div>
  ));

  // Profil
  const [prenom, setPrenom] = useState(user?.prenom || "");
  const [nom, setNom] = useState(user?.nom || "");
  const [instrument, setInstrument] = useState(user?.instrument || "");
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");

  // Groupe
  const [gName, setGName] = useState(group?.name || "");
  const [gStyle, setGStyle] = useState(group?.style || "bagad");
  const [gCustom, setGCustom] = useState(group?.custom_style || "");
  const [gDisc, setGDisc] = useState(group?.disciplines || []);
  const [gLang, setGLang] = useState(group?.language || "fr");
  const [instruments, setInstruments] = useState(group?.instruments || []);
  // Phase 7 — changement d'adresse e-mail (comptes identité uniquement)
  const [newEmail, setNewEmail] = useState("");
  const [emailPwd, setEmailPwd] = useState("");
  const [emailStep, setEmailStep] = useState(1);
  const [emailCode, setEmailCode] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const startEmailChange = async () => {
    setEmailBusy(true);
    try {
      const { data } = await api.post("/auth/change-email/start", { new_email: newEmail, password: emailPwd, lang });
      setEmailStep(2);
      setEmailCode("");
      toast.success(t("param.email_code_sent", { email: data.email }));
    } catch (e) { toast.error(e.friendly); }
    finally { setEmailBusy(false); }
  };

  const verifyEmailChange = async () => {
    const code = emailCode.replace(/\D/g, "");
    if (code.length !== 6) { toast.error(t("auth.code_incomplete")); return; }
    setEmailBusy(true);
    try {
      await api.post("/auth/change-email/verify", { code });
      toast.success(t("param.email_changed"));
      setNewEmail(""); setEmailPwd(""); setEmailCode(""); setEmailStep(1);
      refresh();
    } catch (e) { toast.error(e.friendly); }
    finally { setEmailBusy(false); }
  };
  const [newInstr, setNewInstr] = useState("");
  const [subs, setSubs] = useState(group?.event_subcategories || []);
  const [newSubLabel, setNewSubLabel] = useState({ repetition: "", prestation: "", autre: "" });
  const [progAxes, setProgAxes] = useState(group?.program_axes || []);  const [newAxis, setNewAxis] = useState("");
  const [notifPrefs, setNotifPrefs] = useState({ pieces: true, events_created: true, events_updated: true, documents: true });
  const [pushState, setPushState] = useState("off");
  const [calToken, setCalToken] = useState(null);
  const [fontScale, setFontScale] = useState(() => Number(localStorage.getItem("rannea_font_scale")) || 1);
  const [showTuto, setShowTuto] = useState(false);

  // Retour Stripe Checkout : confirme le paiement côté serveur puis rafraîchit l'abonnement
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (params.get("cancel")) toast.info(t("param.payment_cancelled"));
    if (params.get("fee_cancel")) toast.info(t("myfees.payment_cancelled"));
    const feeSid = params.get("fee_session");
    if (feeSid) {
      let stoppedFee = false;
      (async () => {
        for (let i = 0; i < 10 && !stoppedFee; i++) {
          const r = await api.get(`/fees/checkout/status/${feeSid}`).catch(() => null);
          if (r?.data?.status === "paid") {
            toast.success(t("myfees.payment_success"));
            window.history.replaceState({}, "", "/parametres?tab=profil");
            window.location.reload();
            break;
          }
          await new Promise((res) => setTimeout(res, 2000));
        }
      })();
      window.history.replaceState({}, "", "/parametres?tab=profil");
      return () => { stoppedFee = true; };
    }
    if (!sid) return;
    let stopped = false;
    (async () => {
      for (let i = 0; i < 10 && !stopped; i++) {
        const r = await api.get(`/group/subscription/checkout/status/${sid}`).catch(() => null);
        if (r?.data?.payment_status === "paid") {
          toast.success(t("param.payment_success"));
          await refresh();
          break;
        }
        await new Promise((res) => setTimeout(res, 2000));
      }
    })();
    window.history.replaceState({}, "", "/parametres?tab=profil");
    return () => { stopped = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [fontScaleTmp, setFontScaleTmp] = useState(fontScale);
  const fontScaleSaved = useRef(fontScale);

  useEffect(() => {
    api.get("/display/prefs").then((r) => {
      const v = r.data.font_scale || 1;
      setFontScale(v); setFontScaleTmp(v); fontScaleSaved.current = v;
      document.documentElement.style.setProperty("--font-scale", String(v));
    }).catch(() => {});
  }, []);

  // Quitter sans Appliquer → rétablit la valeur enregistrée
  useEffect(() => () => {
    document.documentElement.style.setProperty("--font-scale", String(fontScaleSaved.current));
  }, []);

  const previewFontScale = (v) => {
    setFontScaleTmp(v);
    document.documentElement.style.setProperty("--font-scale", String(v));
  };

  const applyDisplayScale = async () => {
    try {
      const r = await api.put("/display/prefs", { font_scale: fontScaleTmp });
      const v = r.data.prefs.font_scale;
      setFontScale(v); setFontScaleTmp(v); fontScaleSaved.current = v;
      localStorage.setItem("rannea_font_scale", String(v));
      document.documentElement.style.setProperty("--font-scale", String(v));
      toast.success(t("param.font_applied"));
    } catch (e) { toast.error(e.friendly); }
  };

  // Thème
  const [theme, setTheme] = useState(group?.theme || {});

  useEffect(() => {
    if (group) {
      setGName(group.name); setGStyle(group.style); setGCustom(group.custom_style || "");
      setGDisc(group.disciplines || []);
      setGLang(group.language); setInstruments(group.instruments || []);
      setTheme(group.theme || {});
      setSubs(group.event_subcategories || []);
      setProgAxes(group.program_axes || []);
    }
  }, [group]);

  const saveProfile = async () => {
    try {
      await api.put("/members/me", { prenom, nom, instrument });
      toast.success(t("param.profile_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const changePassword = async () => {
    if (newPwd !== confirmPwd) { toast.error(t("param.pwd_mismatch")); return; }
    try {
      await api.post("/auth/change-password", { old_password: oldPwd, new_password: newPwd });
      toast.success(t("param.pwd_changed"));
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e) { toast.error(e.friendly); }
  };

  const saveGroup = async () => {
    try {
      await api.put("/group", { name: gName, style: gStyle, custom_style: gCustom, language: gLang, disciplines: gDisc });
      toast.success(t("param.group_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const CATEGORY_IMAGES = [
    { key: "repetition", label: "cat.repetition" },
    { key: "prestation", label: "cat.prestation" },
    { key: "autre", label: "cat.autre" },
  ];
  const [imgTick, setImgTick] = useState(Date.now());
  const [catImgOk, setCatImgOk] = useState({});
  const removeCategoryImage = async (cat) => {
    try {
      await api.delete(`/group/category-image/${cat}`);
      setCatImgOk((p) => ({ ...p, [cat]: false }));
      setImgTick(Date.now());
      toast.success(t("param.catimg_removed"));
    } catch (e) { toast.error(e.friendly); }
  };
  const uploadCategoryImage = async (cat, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await uploadWithProgress(`catimg-${cat}`, `/group/category-image/${cat}`, fd);
      toast.success(t("param.catimg_updated"));
      setImgTick(Date.now());
    } catch (e) { toast.error(e.friendly); }
  };

  const uploadLogo = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await uploadWithProgress("logo", "/group/logo", fd);
      toast.success(t("param.logo_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const saveInstruments = async (list) => {
    setInstruments(list);
    try {
      await api.put("/group/instruments", { instruments: list });
      toast.success(t("param.instruments_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const saveSubs = async (list) => {
    setSubs(list);
    try {
      await api.put("/group/subcategories", { subcategories: list });
      toast.success(t("param.subs_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const saveAxes = async (list) => {
    setProgAxes(list);
    try {
      await api.put("/group/program-axes", { axes: list });
      toast.success(t("param.axes_updated"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  useEffect(() => {
    api.get("/notifications/prefs").then((r) => setNotifPrefs(r.data)).catch(() => {});
    api.get("/calendar/token").then((r) => setCalToken(r.data.token)).catch(() => {});
    if (!pushSupported()) setPushState("unsupported");
    else if (Notification.permission === "granted") setPushState("granted");
    else if (Notification.permission === "denied") setPushState("denied");
  }, []);

  const savePrefs = async (next) => {
    setNotifPrefs(next);
    try {
      await api.put("/notifications/prefs", next);
    } catch (e) { toast.error(e.friendly); }
  };

  const enablePush = async () => {
    try {
      await subscribeToPush();
      setPushState("granted");
      toast.success(t("param.push_enabled"));
    } catch (e) {
      toast.error(e.message || t("param.push_failed"));
      if (typeof Notification !== "undefined" && Notification.permission === "denied") setPushState("denied");
    }
  };

  const previewTheme = (t) => {
    setTheme(t);
    applyTheme(t);
  };

  const applyAndSave = async () => {
    try {
      await api.put("/group/theme", { theme });
      toast.success(t("param.theme_applied"));
      refresh();
    } catch (e) { toast.error(e.friendly); }
  };

  const pickColor = async (key) => {
    if (window.EyeDropper) {
      try {
        const res = await new window.EyeDropper().open();
        previewTheme({ ...theme, [key]: res.sRGBHex });
      } catch {}
    } else {
      toast.info(t("param.eyedropper_unsupported"));
    }
  };

  return (
    <div className="space-y-4" data-testid="settings-page">
      <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Settings className="h-6 w-6 brand" /> {t("nav.settings")}</h2>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="card-bg border border-app flex-wrap h-auto">
          <TabsTrigger value="profil" data-testid="tab-profil"><User className="h-4 w-4 mr-1" /> {t("param.tab_profile")}</TabsTrigger>
          {isAdmin && <TabsTrigger value="groupe" data-testid="tab-groupe"><Building2 className="h-4 w-4 mr-1" /> {t("param.tab_group")}</TabsTrigger>}
          {isAdmin && <TabsTrigger value="theme" data-testid="tab-theme"><Palette className="h-4 w-4 mr-1" /> {t("param.tab_theme")}</TabsTrigger>}
          {canManageFees && group?.kind !== "personal" && <TabsTrigger value="cotisations" data-testid="tab-cotisations"><HandCoins className="h-4 w-4 mr-1" /> {t("fees.tab")}</TabsTrigger>}
        </TabsList>

        {/* PROFIL */}
        <TabsContent value="profil" className="mt-4 space-y-4">
          <div className="app-card p-6 space-y-4 max-w-lg">
            <h3 className="font-heading text-lg font-bold">{t("param.my_profile")}</h3>
            <p className="text-xs t-secondary font-mono">{user?.login_id} — {t("param.role_hint")}</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("firstname")}</Label>
                <Input value={prenom} onChange={(e) => setPrenom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="profile-prenom-input" />
              </div>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("lastname")}</Label>
                <Input value={nom} onChange={(e) => setNom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="profile-nom-input" />
              </div>
            </div>
            {!isPersonal && (
            <div>
              <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.instrument_one")}</Label>
              {(group?.instruments || []).length > 0 ? (
                <Select value={instrument} onValueChange={setInstrument}>
                  <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="profile-instrument-select"><SelectValue placeholder={t("usr.choose")} /></SelectTrigger>
                  <SelectContent>{group.instruments.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input value={instrument} onChange={(e) => setInstrument(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="profile-instrument-input" />
              )}
            </div>
            )}
            <Button className="brand-bg text-white" onClick={saveProfile} data-testid="profile-save-button">{t("common.save")}</Button>
          </div>

          {identity?.id && (
          <div className="app-card p-6 space-y-4 max-w-lg" data-testid="email-card">
            <h3 className="font-heading text-lg font-bold">{t("param.email_title")}</h3>
            <p className="text-sm t-secondary">{t("param.email_current")} : <span className="font-medium" data-testid="current-email">{identity.email}</span></p>
            {emailStep === 1 ? (
              <>
                <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder={t("param.email_new")} className="bg-transparent border-app" data-testid="new-email-input" />
                <Input type="password" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} placeholder={t("param.pwd_current")} className="bg-transparent border-app" data-testid="email-password-input" />
                <Button className="brand-bg text-white" onClick={startEmailChange} disabled={emailBusy || !newEmail || !emailPwd} data-testid="email-send-code-button">{t("param.email_send_code")}</Button>
              </>
            ) : (
              <>
                <Input value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" inputMode="numeric" className="bg-transparent border-app text-center tracking-[0.5em] text-lg" data-testid="email-code-input" />
                <div className="flex gap-2">
                  <Button variant="outline" className="border-app" onClick={() => { setEmailStep(1); setEmailCode(""); }} data-testid="email-back-button">{t("login.migrate_cancel")}</Button>
                  <Button className="brand-bg text-white flex-1" onClick={verifyEmailChange} disabled={emailBusy} data-testid="email-verify-button">{t("param.email_verify")}</Button>
                </div>
              </>
            )}
            <p className="text-xs t-secondary">{t("param.email_hint")}</p>
          </div>
          )}

          <div className="app-card p-6 space-y-4 max-w-lg">
            <h3 className="font-heading text-lg font-bold">{t("param.password")}</h3>
            <Input type="password" value={oldPwd} onChange={(e) => setOldPwd(e.target.value)} placeholder={t("param.pwd_current")} className="bg-transparent border-app" data-testid="old-password-input" />
            <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} placeholder={t("param.pwd_new")} className="bg-transparent border-app" data-testid="new-password-input" />
            <Input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} placeholder={t("confirmation")} className="bg-transparent border-app" data-testid="confirm-password-input" />
            <Button className="brand-bg text-white" onClick={changePassword} disabled={!oldPwd || !newPwd} data-testid="change-password-button">{t("param.change_password")}</Button>
          </div>

          {!tspace && !isPersonal && (
          <div className="app-card p-6 space-y-4 max-w-lg" data-testid="notifications-card">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Bell className="h-5 w-5 accent" /> {t("layout.notifications")}</h3>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{t("param.push_title")}</p>
                <p className="text-xs t-secondary">{t("param.push_desc")}</p>
              </div>
              {pushState === "granted" ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" data-testid="push-status-on">{t("param.push_on")}</span>
              ) : pushState === "denied" ? (
                <span className="text-xs t-secondary" data-testid="push-status-denied">{t("param.push_denied")}</span>
              ) : pushState === "unsupported" ? (
                <span className="text-xs t-secondary" data-testid="push-status-unsupported">{t("param.push_unsupported")}</span>
              ) : (
                <Button size="sm" className="brand-bg text-white" onClick={enablePush} data-testid="push-enable-button">{t("param.activate")}</Button>
              )}
            </div>
            <div className="border-t border-app pt-3 space-y-2.5">
              <p className="text-xs uppercase tracking-wider t-secondary">{t("param.notify_for")}</p>
              {[
                ["pieces", "param.notif_pieces"],
                ["documents", "param.notif_documents"],
                ["events_created", "param.notif_events_created"],
                ["events_updated", "param.notif_events_updated"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center justify-between text-sm cursor-pointer" data-testid={`notif-pref-row-${k}`}>
                  {t(label)}
                  <Switch checked={notifPrefs[k]} onCheckedChange={(v) => savePrefs({ ...notifPrefs, [k]: v })} data-testid={`notif-pref-${k}`} />
                </label>
              ))}
            </div>
          </div>
          )}

          {!tspace && !isPersonal && calToken && (
          <div className="app-card p-6 space-y-4 max-w-lg" data-testid="calendar-card">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><CalendarSync className="h-5 w-5 accent" /> {t("param.cal_title")}</h3>
            <p className="text-xs t-secondary">{t("param.cal_desc")}</p>
            <a
              href={`webcal://${window.location.host}/api/calendar/${calToken}.ics`}
              className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-lg brand-bg text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              data-testid="calendar-subscribe-button"
            >
              <CalendarSync className="h-4 w-4" /> {t("param.cal_button")}
            </a>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={`${window.location.origin}/api/calendar/${calToken}.ics`}
                className="bg-transparent border-app text-xs font-mono"
                data-testid="calendar-link-input"
                onFocus={(e) => e.target.select()}
              />
              <Button
                size="icon"
                variant="outline"
                className="border-app shrink-0"
                data-testid="calendar-copy-button"
                title={t("agd.copy_link")}
                aria-label={t("agd.copy_link")}
                onClick={async () => {
                  const url = `${window.location.origin}/api/calendar/${calToken}.ics`;
                  try { await navigator.clipboard.writeText(url); } catch { /* presse-papiers indisponible */ }
                  toast.success(t("param.cal_copied"));
                }}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs t-secondary">{t("param.cal_hint")}</p>
          </div>
          )}

          <div className="app-card p-6 space-y-4 max-w-lg" data-testid="display-card">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><Type className="h-5 w-5 accent" /> {t("param.display")}</h3>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">{t("param.language_label")}</p>
                <p className="text-xs t-secondary">{t("param.language_hint")}</p>
              </div>
              <Select value={lang} onValueChange={setLang}>
                <SelectTrigger className="w-32 shrink-0 bg-transparent border-app" data-testid="language-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fr" data-testid="language-fr">Français</SelectItem>
                  <SelectItem value="en" data-testid="language-en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">{t("param.font_size")}</p>
                <span className="text-xs font-bold accent" data-testid="display-font-scale-value">{Math.round(fontScaleTmp * 100)}%</span>
              </div>
              <input
                type="range" min={0.85} max={1.2} step={0.05} value={fontScaleTmp}
                onChange={(e) => previewFontScale(parseFloat(e.target.value))}
                className="w-full accent-emerald-500"
                data-testid="display-font-scale-slider"
              />
              <div className="flex justify-between text-xs t-secondary mt-1"><span>85 %</span><span>100 %</span><span>120 %</span></div>
            </div>
            <p className="text-xs t-secondary">{t("param.font_size_hint")}</p>
            <Button className="brand-bg text-white" onClick={applyDisplayScale} disabled={fontScaleTmp === fontScale} data-testid="display-apply-button">{t("param.apply")}</Button>
          </div>

          {!isPersonal && <MyFeesCard />}

          <div className="app-card p-6 space-y-4 max-w-lg" data-testid="tutorial-card">
            <h3 className="font-heading text-lg font-bold flex items-center gap-2"><GraduationCap className="h-5 w-5 accent" /> {t("param.tutorial_title")}</h3>
            <p className="text-xs t-secondary">{t("param.tutorial_desc")}</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="border-app" onClick={() => setShowTuto(true)} data-testid="replay-tutorial-button">{t("param.tutorial_button")}</Button>
              <Button variant="outline" className="border-app" onClick={() => navigate("/aide")} data-testid="help-center-link-button">{t("help.title")}</Button>
            </div>
          </div>

          <Button variant="outline" className="border-app text-rose-400" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" /> {t("common.logout")}
          </Button>
          <OfflineCard />
          {showTuto && <TutorialCarousel onClose={() => setShowTuto(false)} />}
        </TabsContent>

        {/* GROUPE */}
        {isPersonal && (
          <TabsContent value="groupe" className="mt-4 space-y-4">
            <JoinGroupCard />
          </TabsContent>
        )}
        {isAdmin && !isPersonal && (
          <TabsContent value="groupe" className="mt-4 space-y-4">
            <JoinRequestsAdminCard />
            <div className="app-card p-6 space-y-4 max-w-lg">
              <h3 className="font-heading text-lg font-bold">{t("param.group_identity")}</h3>
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("usr.col_name")}</Label>
                <Input value={gName} onChange={(e) => setGName(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="group-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("style_label")}</Label>
                  <Select value={gStyle} onValueChange={setGStyle}>
                    <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="group-style-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pipeband">Pipe Band</SelectItem>
                      <SelectItem value="bagad">Bagad</SelectItem>
                      <SelectItem value="autre">{t("cat.autre")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("language")}</Label>
                  <Select value={gLang} onValueChange={setGLang}>
                    <SelectTrigger className="mt-1 bg-transparent border-app" data-testid="group-language-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">Français</SelectItem>
                      <SelectItem value="en">Anglais</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {gStyle === "autre" && (
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("custom_style")}</Label>
                  <Input value={gCustom} onChange={(e) => setGCustom(e.target.value)} className="mt-1 bg-transparent border-app" data-testid="group-custom-style-input" />
                </div>
              )}
              {gStyle !== "autre" && (
                <div>
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.disciplines_label")}</Label>
                  <div className="flex gap-4 mt-2" data-testid="group-disciplines">
                    {["bagad", "pipeband"].map((d) => (
                      <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          className="accent-emerald-500"
                          checked={gDisc.includes(d)}
                          onChange={(e) => setGDisc(e.target.checked ? [...gDisc, d] : gDisc.filter((x) => x !== d))}
                          data-testid={`discipline-check-${d}`}
                        />
                        {d === "bagad" ? "Bagad" : "Pipe Band"}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs t-secondary mt-1">{t("param.disciplines_hint")}</p>
                </div>
              )}
              <div>
                <Label className="t-secondary text-xs uppercase tracking-wider">{t("param.logo_label")}</Label>
                <div className="flex items-center gap-3 mt-2">
                  <img src={logoUrl(Date.now())} alt="Logo" className="h-14 w-14 rounded-lg object-cover border border-app" onError={(e) => { e.target.style.display = "none"; }} data-testid="group-logo-preview" />
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadLogo(e.target.files[0])} data-testid="group-logo-input" />
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-app text-sm hover:bg-white/5">
                      <Upload className="h-4 w-4" /> {t("param.change_logo")}
                    </span>
                  </label>
                </div>
                <UploadBar k="logo" />
              </div>
              <Button className="brand-bg text-white" onClick={saveGroup} data-testid="group-save-button">{t("common.save")}</Button>
            </div>

            <div className="app-card p-6 space-y-4 max-w-lg">
              <h3 className="font-heading text-lg font-bold">{t("param.catimg_title")}</h3>
              <p className="text-xs t-secondary">{t("param.catimg_desc")}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="category-images-list">
                {CATEGORY_IMAGES.map((c) => (
                  <div key={c.key} className="space-y-1">
                    <Label className="t-secondary text-xs uppercase tracking-wider">{t(c.label)}</Label>
                    <div className="flex items-center gap-2">
                      <img
                        src={withToken(`${API}/group/category-image/${c.key}?t=${imgTick}`)}
                        alt={t(c.label)}
                        className="h-12 w-16 rounded-md object-cover border border-app"
                        onLoad={(e) => { e.target.style.display = ""; setCatImgOk((p) => ({ ...p, [c.key]: true })); }}
                        onError={(e) => { e.target.style.display = "none"; setCatImgOk((p) => ({ ...p, [c.key]: false })); }}
                        data-testid={`catimg-preview-${c.key}`}
                      />
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadCategoryImage(c.key, e.target.files[0])} data-testid={`catimg-input-${c.key}`} />
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-app text-xs hover:bg-white/5">
                          <Upload className="h-3 w-3" /> {t("param.change")}
                        </span>
                      </label>
                      {catImgOk[c.key] && (
                        <button onClick={() => removeCategoryImage(c.key)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-app text-xs t-secondary hover:text-rose-400 hover:border-rose-400/40 transition-colors" data-testid={`catimg-remove-${c.key}`}>
                          <X className="h-3 w-3" /> {t("param.catimg_remove")}
                        </button>
                      )}
                    </div>
                    <UploadBar k={`catimg-${c.key}`} />
                  </div>
                ))}
              </div>
            </div>

            <div className="app-card p-6 space-y-4 max-w-lg">
              <h3 className="font-heading text-lg font-bold">{t("param.subs_title")}</h3>
              <p className="text-xs t-secondary">{t("param.subs_desc")}</p>
              {EVENT_MAINS.map((mc) => (
                <div key={mc.k} className="space-y-2">
                  <Label className="t-secondary text-xs uppercase tracking-wider">{t(mc.l)}</Label>
                  <div className="space-y-1.5" data-testid={`subcat-list-${mc.k}`}>
                    {subs.filter((s) => s.main === mc.k).map((s) => (
                      <div key={s.id} className="flex items-center gap-2">
                        <Input
                          value={s.label}
                          onChange={(e) => setSubs(subs.map((x) => (x.id === s.id ? { ...x, label: e.target.value } : x)))}
                          onBlur={() => saveSubs(subs.filter((x) => x.label.trim()))}
                          className="bg-transparent border-app h-9"
                          data-testid={`subcat-label-${s.id}`}
                        />
                        <button onClick={() => saveSubs(subs.filter((x) => x.id !== s.id))} className="t-secondary hover:text-rose-400 shrink-0" data-testid={`subcat-delete-${s.id}`}>
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={newSubLabel[mc.k]}
                      onChange={(e) => setNewSubLabel({ ...newSubLabel, [mc.k]: e.target.value })}
                      placeholder={t("param.sub_new_placeholder", { cat: t(mc.l).toLowerCase() })}
                      className="bg-transparent border-app h-9"
                      data-testid={`subcat-new-${mc.k}`}
                    />
                    <Button
                      className="brand-bg text-white h-9 shrink-0"
                      disabled={!newSubLabel[mc.k].trim()}
                      onClick={() => { saveSubs([...subs, { main: mc.k, label: newSubLabel[mc.k].trim() }]); setNewSubLabel({ ...newSubLabel, [mc.k]: "" }); }}
                      data-testid={`subcat-add-${mc.k}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="app-card p-6 space-y-4 max-w-lg">
              <h3 className="font-heading text-lg font-bold">{t("param.axes_title")}</h3>
              <p className="text-xs t-secondary">{t("param.axes_desc")}</p>
              <div className="flex flex-wrap gap-2" data-testid="axes-list">
                {progAxes.map((a) => (
                  <span key={a} className="inline-flex items-center gap-1 text-sm border border-app rounded-full px-3 py-1">
                    {a}
                    <button onClick={() => saveAxes(progAxes.filter((x) => x !== a))} className="t-secondary hover:text-rose-400" data-testid={`remove-axis-${a.replace(/\s/g, "-")}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newAxis} onChange={(e) => setNewAxis(e.target.value)} placeholder={t("param.axis_new_placeholder")} className="bg-transparent border-app" data-testid="new-axis-input" />
                <Button className="brand-bg text-white" disabled={!newAxis.trim()} onClick={() => { if (!progAxes.includes(newAxis.trim())) saveAxes([...progAxes, newAxis.trim()]); setNewAxis(""); }} data-testid="add-axis-button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="app-card p-6 space-y-4 max-w-lg">
              <h3 className="font-heading text-lg font-bold">{t("param.instruments_title")}</h3>
              <div className="flex flex-wrap gap-2" data-testid="instruments-list">
                {instruments.map((i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-sm border border-app rounded-full px-3 py-1">
                    {i}
                    <button onClick={() => saveInstruments(instruments.filter((x) => x !== i))} className="t-secondary hover:text-rose-400" data-testid={`remove-instrument-${i}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newInstr} onChange={(e) => setNewInstr(e.target.value)} placeholder={t("param.instrument_new_placeholder")} className="bg-transparent border-app" data-testid="new-instrument-input" />
                <Button className="brand-bg text-white" disabled={!newInstr.trim()} onClick={() => { if (!instruments.includes(newInstr.trim())) saveInstruments([...instruments, newInstr.trim()]); setNewInstr(""); }} data-testid="add-instrument-button">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </TabsContent>
        )}

        {/* THÈME */}
        {isAdmin && (
          <TabsContent value="theme" className="mt-4 space-y-4">
            <div className="app-card p-6 space-y-4 max-w-2xl">
              <h3 className="font-heading text-lg font-bold">{t("param.theme_title")}</h3>
              <div className="flex flex-wrap gap-2">
                {PALETTES.map((p) => (
                  <button key={p.name} onClick={() => previewTheme(p.theme)} className="text-xs border border-app rounded-full px-3 py-1.5 hover:bg-white/5" data-testid={`palette-${p.name.replace(/\s/g, "-")}`}>
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="theme-fields">
                {THEME_FIELDS.map((f) => (
                  <div key={f.k} className="space-y-1.5">
                    <Label className="t-secondary text-xs">{t(f.l)}</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={theme[f.k] || "#000000"}
                        onChange={(e) => previewTheme({ ...theme, [f.k]: e.target.value })}
                        className="h-9 w-12 rounded cursor-pointer bg-transparent border border-app"
                        data-testid={`theme-color-${f.k}`}
                      />
                      <button onClick={() => pickColor(f.k)} className="text-[10px] t-secondary hover:text-white border border-app rounded px-1.5 py-1" title={t("param.eyedropper")} data-testid={`theme-eyedropper-${f.k}`}>
                        {t("param.eyedropper")}
                      </button>
                    </div>
                    <p className="text-[10px] font-mono t-secondary">{theme[f.k]}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs t-secondary">{t("param.theme_hint")}</p>
              <Button className="brand-bg text-white" onClick={applyAndSave} data-testid="apply-theme-button">
                <Check className="h-4 w-4 mr-1" /> {t("param.apply_caps")}
              </Button>
            </div>
          </TabsContent>
        )}

        {/* COTISATIONS (admin + trésorier) */}
        {canManageFees && (
          <TabsContent value="cotisations" className="mt-4 space-y-4">
            <FeesConfig />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
